import React, { ReactNode, useId } from 'react';
import { FieldError } from 'react-hook-form';
import { ExclamationCircleIcon, ExclamationTriangleIcon, MinusCircleIcon } from '@heroicons/react/24/outline';

import { cn } from 'support/helpers/cn/cn';
import { FieldState } from 'support/types/ui';
import { WithRef } from 'support/types/withRef';

import { InputLabel } from '../InputLabel/InputLabel';

const wrapperTextClassByState: Record<FieldState, string> = {
  default: '',
  error: 'text-red-700 focus-within:text-inherit',
  warning: 'text-warning-700 focus-within:text-inherit',
  'pending-validation': 'text-blue-700 focus-within:text-inherit',
};

const textareaClassByState: Record<FieldState, string> = {
  default: 'focus-visible:ring-procuros-green-500 focus-visible:border-procuros-green-500',
  error:
    'bg-red-50 placeholder:text-red-700 focus-visible:ring-red-500 focus-visible:border-red-500 focus:bg-transparent',
  warning:
    'bg-warning-50 placeholder:text-warning-700 focus-visible:ring-warning-500 focus-visible:border-warning-500 focus:bg-transparent',
  'pending-validation':
    'bg-blue-50 placeholder:text-blue-700 focus-visible:ring-blue-500 focus-visible:border-blue-500 focus:bg-transparent',
};

const trailingIconByState: Record<FieldState, ReactNode> = {
  default: null,
  error: (
    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
      <ExclamationCircleIcon className="size-5 text-red-500" aria-hidden="true" />
    </div>
  ),
  warning: (
    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
      <ExclamationTriangleIcon className="size-5 text-warning-500" aria-hidden="true" />
    </div>
  ),
  'pending-validation': (
    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
      <MinusCircleIcon className="size-5 text-blue-500" aria-hidden="true" />
    </div>
  ),
};

type TextareaVariantProps =
  | { variant?: 'default'; valueDescription?: never }
  | { variant: 'large'; valueDescription?: string };

type TextareaType = TextareaVariantProps & {
  name?: string;
  label?: string;
  rows?: number;
  errors?: FieldError;
  hasErrors?: boolean;
  fieldState?: FieldState;
  wrapperClassName?: string;
  wrapperRef?: React.Ref<HTMLDivElement>;
  required?: boolean;
  value?: string;
  className?: string;
  description?: ReactNode;
} & Omit<React.InputHTMLAttributes<HTMLTextAreaElement>, 'size'>;

const Textarea = ({
  variant = 'default',
  valueDescription,
  description,
  label,
  required,
  rows = 3,
  value,
  name,
  errors,
  hasErrors,
  fieldState,
  wrapperClassName,
  wrapperRef,
  className,
  ref,
  ...otherProps
}: WithRef<TextareaType, HTMLTextAreaElement>) => {
  const textAreaId = useId();
  const isLarge = variant === 'large';
  const resolvedState: FieldState = fieldState ?? (errors || hasErrors ? 'error' : 'default');

  return (
    <div
      className={cn(wrapperClassName, wrapperTextClassByState[resolvedState], {
        'opacity-40': otherProps.disabled,
      })}
      ref={wrapperRef}
    >
      {/* Label + Description when large */}
      {(label || description) && isLarge ? (
        <div className="mb-2 flex flex-col gap-1">
          <InputLabel label={label} variant={variant} required={required} />
          {description && <p className="text-sm text-gray-700">{description}</p>}
        </div>
      ) : (
        <InputLabel label={label} variant={variant} required={required} htmlFor={textAreaId} />
      )}

      {/* Value Description + Textarea */}
      {valueDescription && <p className="mb-1 text-sm text-gray-500">{valueDescription}</p>}

      <div className={cn('relative rounded-md shadow-xs', !isLarge && label ? 'mt-1' : undefined)}>
        <textarea
          {...otherProps}
          ref={ref}
          name={name}
          id={textAreaId}
          className={cn(
            'data-hj-allow box-border block w-full rounded-md border-gray-300 shadow-xs focus-visible:outline-hidden sm:text-sm',
            textareaClassByState[resolvedState],
            className,
          )}
          rows={rows}
          value={value}
        />

        {trailingIconByState[resolvedState]}
      </div>

      {!!errors && <p className="mt-2 text-sm text-red-600">{errors.message}</p>}
      {/* when default size, show description bellow textarea */}
      {!!description && !isLarge && <p className="mt-2 text-sm text-gray-500">{description}</p>}
    </div>
  );
};

export { Textarea };
